// FILE: src/components/session/SectionCheckpoint.js
//
// Pre-break "last chance" checkpoint for Full Exam mode, plus the shared
// chip list reused by the End Exam confirmation modal.
//
// When the learner moves forward past a break threshold with flagged or
// unanswered questions in the section that is about to lock, App.js shows
// this modal INSTEAD of the break offer. Clicking a chip jumps straight to
// that question (the pending break stays armed — see handleNext); clicking
// "Continue to Break" proceeds with the original move, which seals the
// section permanently (taken OR skipped), mirroring the real PMI exam.
//
// Presentational only — all state lives in App.js.

import React from 'react';

// Colours match QuestionNavigator's status palette so the chips read the
// same way everywhere: orange = unanswered/skipped, blue = flagged.
const CHIP_COLORS = {
  unanswered:         { bg: '#e07b00', border: '#e07b00', text: '#fff' },
  flagged:            { bg: '#1565c0', border: '#1565c0', text: '#fff' },
  'unanswered-flagged': { bg: '#e07b00', border: '#1565c0', text: '#fff' },
};

/**
 * Pure helper — unresolved questions in [rangeStart, rangeEnd).
 * A question is unresolved when it is flagged OR not yet submitted.
 * Every index below a break threshold has been processed (Next or Skip),
 * so "not submitted" covers skipped and jump-away-unanswered questions.
 *
 * @returns {Array<{index:number, flagged:boolean, unanswered:boolean}>}
 */
export function computeUnresolvedRange({ rangeStart, rangeEnd, questionStates, flaggedIndices }) {
  const out = [];
  for (let i = rangeStart; i < rangeEnd; i++) {
    const answered = questionStates[i]?.submitted === true;
    const flagged = flaggedIndices.has(i);
    if (!answered || flagged) out.push({ index: i, flagged, unanswered: !answered });
  }
  return out;
}

const chipStyles = {
  wrap: {
    display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center',
    maxHeight: '132px', overflowY: 'auto', padding: '4px 2px', margin: '14px 0 6px',
  },
  chip: (colors) => ({
    minWidth: '38px', height: '34px', padding: '0 6px',
    borderRadius: '17px', border: `2px solid ${colors.border}`,
    background: colors.bg, color: colors.text,
    fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
  }),
  legend: {
    display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap',
    fontSize: '0.75rem', color: '#666', margin: '4px 0 0',
  },
  legendSwatch: (colors) => ({
    display: 'inline-block', width: '12px', height: '12px', borderRadius: '6px',
    background: colors.bg, border: `2px solid ${colors.border}`,
    marginRight: '5px', verticalAlign: '-1px',
  }),
};

function chipColors(item) {
  if (item.unanswered && item.flagged) return CHIP_COLORS['unanswered-flagged'];
  if (item.unanswered) return CHIP_COLORS.unanswered;
  return CHIP_COLORS.flagged;
}

/**
 * Shared clickable chip list. `unresolved` items carry 0-based indices;
 * chips display 1-based question numbers.
 */
export function CheckpointChips({ unresolved, onJumpTo }) {
  return (
    <div>
      <div style={chipStyles.wrap} data-testid="checkpoint-chips">
        {unresolved.map((item) => (
          <button
            key={item.index}
            type="button"
            data-testid={`checkpoint-chip-${item.index}`}
            style={chipStyles.chip(chipColors(item))}
            title={`Go to question ${item.index + 1}`}
            onClick={() => onJumpTo(item.index)}
          >
            {item.index + 1}
          </button>
        ))}
      </div>
      <p style={chipStyles.legend}>
        <span><span style={chipStyles.legendSwatch(CHIP_COLORS.unanswered)} />Unanswered</span>
        <span><span style={chipStyles.legendSwatch(CHIP_COLORS.flagged)} />Flagged</span>
        <span><span style={chipStyles.legendSwatch(CHIP_COLORS['unanswered-flagged'])} />Both</span>
      </p>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.65)',
    zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px',
  },
  modal: {
    background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
    padding: '30px 36px', maxWidth: '560px', width: '100%', textAlign: 'center',
  },
  heading: { margin: '0 0 12px', fontSize: '1.35rem', color: '#1a1a2e' },
  body: { margin: '0 0 4px', fontSize: '0.95rem', color: '#444', lineHeight: 1.5 },
  actions: { display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '22px' },
  keepWorking: {
    padding: '10px 22px', borderRadius: '8px', border: '2px solid #1a1a2e',
    background: '#1a1a2e', color: '#fff', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
  },
  proceed: {
    padding: '10px 22px', borderRadius: '8px', border: '2px solid #c62828',
    background: '#fff', color: '#c62828', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
  },
};

/**
 * Pre-break checkpoint modal.
 *
 * @param {number}   afterQuestion  1-based question count of the section
 *                                  about to lock (break threshold).
 * @param {Array}    unresolved     From computeUnresolvedRange().
 * @param {function} onJumpTo       (index) — jump to a question; caller closes.
 * @param {function} onProceed      Continue to the break (locks the section).
 * @param {function} onDismiss      Keep working on the current question.
 */
export default function SectionCheckpoint({ afterQuestion, unresolved, onJumpTo, onProceed, onDismiss }) {
  const n = unresolved.length;
  return (
    <div style={styles.overlay} data-testid="section-checkpoint">
      <div style={styles.modal} role="alertdialog" aria-label="Unresolved questions before break">
        <h3 style={styles.heading}>Before your break…</h3>
        <p style={styles.body}>
          {n} question{n !== 1 ? 's' : ''} in this section {n !== 1 ? 'are' : 'is'} still
          flagged or unanswered. Once the break screen appears — taken <em>or</em> skipped —
          Questions 1–{afterQuestion} lock permanently, just like the real exam.
        </p>
        <CheckpointChips unresolved={unresolved} onJumpTo={onJumpTo} />
        <div style={styles.actions}>
          <button type="button" style={styles.keepWorking} data-testid="checkpoint-keep-working" onClick={onDismiss}>
            Keep Working
          </button>
          <button type="button" style={styles.proceed} data-testid="checkpoint-proceed" onClick={onProceed}>
            Continue to Break
          </button>
        </div>
      </div>
    </div>
  );
}
